// src/models/friendModel.js
import { db, functions } from '../firebase';
import { subscribeToVisiblePorchlights } from './porchlightModel';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, onSnapshot, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Returns a list of friends with their porchlight status and off time
export async function getFriends(uid) {
  // Get all friends from the user's subcollection
  const friendsCollectionRef = collection(db, `users/${uid}/friends`);
  const friendsSnapshot = await getDocs(friendsCollectionRef);
  const friendsList = await Promise.all(friendsSnapshot.docs.map(async (friendDoc) => {
    const friendData = friendDoc.data();
    const friendPorchlightDoc = await getDoc(doc(db, 'porchlights', friendDoc.id));

    // Source of truth: porchlights/{uid}.porchlightUntil, but only if viewer is in visibleTo
    let porchlightUntil = null;
    let porchlightOn = false;
    if (friendPorchlightDoc.exists()) {
      const d = friendPorchlightDoc.data();
      const isVisible = Array.isArray(d?.visibleTo) && d.visibleTo.includes(uid);
      const untilField = d?.porchlightUntil;
      if (isVisible) {
        const until = (untilField && typeof untilField.toDate === 'function')
          ? untilField.toDate()
          : (untilField ? new Date(untilField) : null);
        porchlightUntil = until;
        porchlightOn = !!(until && until > new Date());
      }
    }

    return {
      id: friendDoc.id,
      ...friendData,
      porchlightUntil,
      // Maintain backward-compatible field derived from porchlightUntil, never from users/{uid}
      porchlightOn,
    };
  }));
  return friendsList;
}

// Add a friend request
export async function addFriend(uid, friendEmail, requesterFirstName) {
  const userRef = doc(db, 'users', uid);
  const lowercaseFriendEmail = friendEmail.toLowerCase();
  // Use callable Cloud Function to create the friend request server-side
  const requestFriend = httpsCallable(functions, 'requestFriend');
  await requestFriend({ recipientEmail: lowercaseFriendEmail });
  // Optionally track email locally for UX (not security sensitive)
  await updateDoc(userRef, { potentialFriends: arrayUnion(lowercaseFriendEmail) }).catch(() => {});
}

// Get all friend requests for the user
export async function getFriendRequests(uid) {
  // Query friendRequests where recipientUid == current user's uid
  const q = query(collection(db, 'friendRequests'), where('recipientUid', '==', uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

// Accept a friend request
export async function acceptFriendRequest(requestId) {
  // Modern flow: update status only; Cloud Function will create friend docs
  const friendRequestRef = doc(db, 'friendRequests', requestId);
  await updateDoc(friendRequestRef, { status: 'accepted' });
}

// --- Real-time listeners ---

// Subscribe to real-time friend list updates
export function subscribeToFriends(uid, callback) {
  const friendsCollectionRef = collection(db, `users/${uid}/friends`);
  const state = new Map(); // friendId -> friend object ({ id, ...friendData, porchlightUntil })

  const emit = () => {
    const list = Array.from(state.values());
    callback(list);
  };

  // Single subscription: all porchlights visible to current user
  const visibleUnsub = subscribeToVisiblePorchlights(uid, (visibleMap) => {
    let changed = false;
    state.forEach((cur, friendId) => {
      const until = visibleMap.get(friendId) || null;
      if (cur.porchlightUntil !== until) {
        state.set(friendId, { ...cur, porchlightUntil: until });
        changed = true;
      }
    });
    if (changed) emit();
  });

  const friendsUnsub = onSnapshot(
    friendsCollectionRef,
    (snapshot) => {
      const currentIds = new Set();

      snapshot.docs.forEach((friendDoc) => {
        const id = friendDoc.id;
        currentIds.add(id);
        const friendData = friendDoc.data();
        const prev = state.get(id) || { id };
        state.set(id, { ...prev, id, ...friendData });
      });

      // Remove friends no longer present
      Array.from(state.keys()).forEach((id) => {
        if (!currentIds.has(id)) {
          state.delete(id);
        }
      });

      emit();
    },
    (error) => {
      console.error('Error subscribing to friends list', {
        path: `users/${uid}/friends`,
        code: error.code,
        message: error.message,
      });
    }
  );

  // Return a composite unsubscribe that cleans everything up
  return () => {
    try { friendsUnsub(); } catch (_) {}
    try { if (typeof visibleUnsub === 'function') visibleUnsub(); } catch (_) {}
  };
}

// Subscribe to real-time friend requests
export function subscribeToFriendRequests(uid, callback) {
  const q = query(collection(db, 'friendRequests'), where('recipientUid', '==', uid));
  return onSnapshot(
    q,
    (snapshot) => {
      const requests = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      callback(requests);
    },
    (error) => {
      console.error('Error subscribing to friendRequests', {
        path: `friendRequests where recipientUid == ${uid}`,
        code: error.code,
        message: error.message,
      });
    }
  );
}

// Deny a friend request (mark as denied so it no longer shows as pending)
export async function denyFriendRequest(requestId) {
  const friendRequestRef = doc(db, 'friendRequests', requestId);
  const friendRequestSnap = await getDoc(friendRequestRef);
  if (!friendRequestSnap.exists()) return; // already removed
  await updateDoc(friendRequestRef, { status: 'denied' });
}
